import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  CalendarIcon,
  CheckIcon,
  XMarkIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { CountryFlag } from "@/shared/ui";
import { getCountryName, formatDateShort } from "@/shared/core";
import type { ConsultationWithDetails } from "../types/consultation.types";

interface ConsultationCardProps {
  consultation: ConsultationWithDetails;
  onAccept?: (consultationId: string) => void;
  onReject?: (consultationId: string) => void;
  onClick?: () => void;
}

export const ConsultationCard: React.FC<ConsultationCardProps> = ({
  consultation,
  onAccept,
  onReject,
  onClick,
}) => {
  const { t } = useTranslation();

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAccept?.(consultation.id);
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReject?.(consultation.id);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.consultations.status.pending")}
          </span>
        );
      case "accepted":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-atlantis-100 text-atlantis-800">
            <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.consultations.status.accepted")}
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.consultations.status.rejected")}
          </span>
        );
      case "completed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-tiber-800">
            <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.consultations.status.completed")}
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-slate-800">
            <XCircleIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.consultations.status.cancelled")}
          </span>
        );
      default:
        return null;
    }
  };

  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case "urgent":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
            {t("lawyer.consultations.urgency.urgent")}
          </span>
        );
      case "high":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
            {t("lawyer.consultations.urgency.high")}
          </span>
        );
      case "normal":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-tiber-100 text-tiber-800">
            {t("lawyer.consultations.urgency.normal")}
          </span>
        );
      case "low":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-tiber-100 text-tiber-800">
            {t("lawyer.consultations.urgency.low")}
          </span>
        );
      default:
        return null;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className={`group bg-white rounded-xl p-6 shadow-lg border border-tiber-600 hover:shadow-2xl transition-all duration-300 ${
        onClick ? "cursor-pointer" : ""
      }`}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            {consultation.client_profiles?.profiles?.profile_image_url ? (
              <img
                src={consultation.client_profiles.profiles.profile_image_url}
                alt={
                  consultation.client_profiles.profiles.full_name || "Client"
                }
                className="h-14 w-14 rounded-xl object-cover border-2 border-tiber-100"
              />
            ) : (
              <div className="h-14 w-14 rounded-xl bg-linear-to-br from-amber-500 to-orange-600 flex items-center justify-center border-2 border-tiber-100">
                <span className="text-white font-bold text-lg">
                  {getInitials(
                    consultation.client_profiles?.profiles?.full_name || "N/A"
                  )}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-tiber-900 truncate group-hover:text-atlantis-800 transition-colors">
                {consultation.client_profiles?.profiles?.full_name || "N/A"}
              </h3>
              <p className="text-sm text-tiber-600 truncate mt-1">
                {consultation.client_profiles?.profiles?.email || "N/A"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {getStatusBadge(consultation.status)}
            {getUrgencyBadge(consultation.urgency)}
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-tiber-100 text-tiber-700">
              {t(
                `lawyer.consultations.practiceAreas.${consultation.practice_area}`
              )}
            </span>
            {consultation.client_profiles?.profiles?.country && (
              <div className="flex items-center text-xs text-tiber-600">
                <CountryFlag
                  countryCode={consultation.client_profiles.profiles.country}
                  size="1em"
                  className="mr-1"
                />
                {getCountryName(consultation.client_profiles.profiles.country)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="mb-4">
        <p className="text-sm text-tiber-700 line-clamp-3 leading-relaxed mb-3">
          {consultation.description}
        </p>
        <div className="flex items-center gap-4 text-sm text-tiber-600">
          <div className="flex items-center gap-1">
            <CalendarIcon className="w-4 h-4" />
            <span>{formatDateShort(consultation.created_at)}</span>
          </div>
          {consultation.preferred_contact && (
            <div className="text-xs">
              {t("lawyer.consultations.preferredContact")}:{" "}
              {t(
                `lawyer.consultations.contactMethods.${consultation.preferred_contact}`,
                consultation.preferred_contact
              )}
            </div>
          )}
        </div>
      </div>

      {/* Card Footer - Actions */}
      {consultation.status === "pending" && (
        <div className="flex items-center justify-between pt-4 border-t border-tiber-100">
          <div className="flex items-center gap-2">
            <button
              onClick={handleReject}
              className="flex items-center gap-2 px-4 py-2 bg-linear-to-r from-red-50 to-rose-50 text-red-700 rounded-lg hover:from-red-100 hover:to-rose-100 transition-all font-medium text-sm shadow-sm hover:shadow"
            >
              <XMarkIcon className="w-4 h-4" />
              <span>{t("lawyer.consultations.actions.reject")}</span>
            </button>
            <button
              onClick={handleAccept}
              className="flex items-center gap-2 px-4 py-2 bg-linear-to-r from-atlantis-50 to-atlantis-100 text-atlantis-700 rounded-lg hover:from-atlantis-100 hover:to-atlantis-200 transition-all font-medium text-sm shadow-sm hover:shadow"
            >
              <CheckIcon className="w-4 h-4" />
              <span>{t("lawyer.consultations.actions.accept")}</span>
            </button>
          </div>
          {onClick && (
            <ChevronRightIcon className="h-5 w-5 text-tiber-400 group-hover:text-tiber-500 transition-colors" />
          )}
        </div>
      )}
      {consultation.status !== "pending" && onClick && (
        <div className="flex items-center justify-end pt-4 border-t border-tiber-100">
          <ChevronRightIcon className="h-5 w-5 text-tiber-400 group-hover:text-tiber-500 transition-colors" />
        </div>
      )}
    </motion.div>
  );
};
